import { createContext, useContext, type ReactNode } from "react";
import { useParams } from "react-router-dom";

/**
 * Which mount of the tenant-scoped API the screens in this subtree talk to.
 *
 * `tenant` is the client's own back-office, authorised by an ACTIVE membership.
 * `staff` is the platform mirror of the very same routes
 * (`/api/<service>/v1/staff/<clientId>/…`, see `routes/devices.ts`), authorised
 * by the SYSADMIN role — which exists because staff are members of their own
 * tenant and of nobody else's, and so cannot pass the membership check at all.
 */
export type ApiScope = "tenant" | "staff";

type ClientScope = {
  /** The client being looked at. */
  clientId: string;
  scope: ApiScope;
  /**
   * The screens below only read: no composer, no "call", no row action that
   * edits or deletes. The client's dashboard and calls as staff see them on the
   * client's file — the read-only staff account (sales) cannot write there
   * anyway, and a SYSADMIN who means to act does it where the client would.
   */
  readOnly?: boolean;
};

/**
 * `null` means "nobody said", and the hooks below then fall back to the
 * `:company` path param — which is the client whose back-office is open, and is
 * what every screen used before there was anywhere else to look from.
 */
const ClientScopeContext = createContext<ClientScope | null>(null);

/**
 * Point a subtree at one client, through one mount of the API.
 *
 * This is what lets the platform's client view mount **the screens the client
 * uses**, unchanged, instead of a second set that renders the same rows and
 * drifts from them. Two things differ between the two places and both are here:
 * which client (the admin route carries it as `:clientId`, next to the staff's
 * own `:company`), and which mount answers.
 *
 * A screen never asks which one it is. It calls {@link useClientId} where it
 * used to read `:company`, and the hooks address the right mount on their own.
 */
export const ClientScopeProvider = ({
  clientId,
  scope,
  readOnly,
  children,
}: ClientScope & { children: ReactNode }) => (
  <ClientScopeContext.Provider value={{ clientId, scope, readOnly }}>
    {children}
  </ClientScopeContext.Provider>
);

/**
 * The scope a provider set, or `null` outside any — for the hooks that must
 * keep resolving the client the way they always did when nobody set one
 * (`useScopedClient`).
 */
export const useClientScope = (): ClientScope | null =>
  useContext(ClientScopeContext);

/** See {@link ClientScope.readOnly}. `false` outside any provider. */
export const useReadOnly = (): boolean =>
  !!useContext(ClientScopeContext)?.readOnly;

/** Default `tenant`, so every existing screen keeps the behaviour it had. */
export const useApiScope = (): ApiScope =>
  useContext(ClientScopeContext)?.scope ?? "tenant";

/**
 * The client this screen is about.
 *
 * Not `useParams().company`: in the platform's client view that param is the
 * *staff member's own* tenant, and the client being looked at is a different
 * one. Reading the param directly is how a screen ends up listing the wrong
 * client's handsets with no error to show for it.
 */
export const useClientId = (): string => {
  const scoped = useContext(ClientScopeContext);
  const { company } = useParams<{ company: string }>();
  return scoped?.clientId || company || "";
};

/**
 * The base path of one tenant's routes in a service, for the given scope.
 *
 * One spelling of it: a screen that builds its URL by hand is a screen that
 * keeps working in the back-office and 403s in the admin view, with nothing on
 * screen saying why.
 */
export const scopedApi = (
  scope: ApiScope,
  service: string,
  clientId: string,
) =>
  scope === "staff"
    ? `/api/${service}/v1/staff/${clientId}`
    : `/api/${service}/v1/${clientId}`;
